import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "font-medium text-gray-600" }
const _hoisted_2 = { class: "sm:col-span-12" }
const _hoisted_3 = { class: "mb-2 block text-sm font-medium text-gray-800" }
const _hoisted_4 = { class: "mt-4 flex flex-col justify-end pb-1.5" }
const _hoisted_5 = { class: "mt-4 flex flex-col justify-end pb-1.5" }
const _hoisted_6 = { class: "mt-1 text-xs text-gray-500" }
const _hoisted_7 = { class: "mt-6 grid grid-cols-2 place-content-around gap-4 text-center" }

import { onMounted, ref, Ref, computed, WritableComputedRef, PropType } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    VueFlowNodeModel,
    MultiSelectOption,
    QueueModel,
} from '@/types';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { ReplyIcon } from '@heroicons/vue/outline';
import { required } from '@/utils/validators';
import { useI18n } from 'vue-i18n';
import store from '@/store';
import { helpers } from '@vuelidate/validators';

interface ITransferFormState {
    queue: null | QueueModel;
    whisper: null | string;
    queueTimeoutSeconds: null | number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TransferToQueue',
  props: {
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
},
  setup(__props) {

const { t } = useI18n();

const props = __props;

const showForm = ref(false);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const queueOptions = computed(
    () => store.getters['flow/' + ConnectFlowModelingGetters.QUEUE_OPTIONS],
);
const formState: Ref<ITransferFormState> = ref({
    queue: null,
    whisper: null,
    queueTimeoutSeconds: null,
});

const queueTimeoutValidationText = t('connectFlow.node.transferToQueue.queueTimeout.validation');
const validQueueTimeout = helpers.withMessage(queueTimeoutValidationText, (value) => {
    if (value === null || value === '' || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    const n = Number(value);
    return Number.isInteger(n) && n >= 5 && n <= 60;
});

const rules = computed<ValidationArgs<ITransferFormState>>(() => {
    const r: ValidationArgs<ITransferFormState> = {
        queue: {
            required,
        },
        whisper: {},
        queueTimeoutSeconds: { validQueueTimeout },
    };
    return r;
});
const v$: Ref<Validation<ValidationArgs<ITransferFormState>>> = useVuelidate(rules, formState);

const nodeText = computed(() => {
    let text = t('connectFlow.node.transferToQueue.empty');
    if (nodeWritable.value.data.related) {
        text = nodeWritable.value.data.related.label;
    } else if (nodeWritable.value.data.textToAudio) {
        text = t('connectFlow.node.transferToQueue.textToAudio');
    }
    return text;
});

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}
async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value) {
        nodeWritable.value.data.related = {
            value: formState.value.queue?.id,
            label: formState.value.queue?.name,
        } as MultiSelectOption;
        nodeWritable.value.data.whisper = formState.value.whisper ?? undefined;
        nodeWritable.value.data.queueTimeoutSeconds =
            formState.value.queueTimeoutSeconds ?? undefined;
        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}

onMounted(() => {
    if (nodeWritable.value.data.related !== null) {
        formState.value.queue = queueOptions.value.find(
            (queue: QueueModel) => queue.id == nodeWritable.value.data.related?.value,
        );
    }

    if (nodeWritable.value.data.whisper !== undefined) {
        formState.value.whisper = nodeWritable.value.data.whisper;
    }

    if (nodeWritable.value.data.queueTimeoutSeconds !== undefined) {
        formState.value.queueTimeoutSeconds = nodeWritable.value.data.queueTimeoutSeconds;
    }

    v$.value.$validate().then((result) => {
        isFormCorrect.value = result;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    });
});

return (_ctx: any,_cache: any) => {
  const _component_UiNode = _resolveComponent("UiNode")!
  const _component_UiMultiselect = _resolveComponent("UiMultiselect")!
  const _component_UiTextArea = _resolveComponent("UiTextArea")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiRightPanel = _resolveComponent("UiRightPanel")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_UiNode, {
      nodeId: __props.node.id,
      title: _ctx.$t('connectFlow.node.transferToQueue.title'),
      icon: _unref(ReplyIcon),
      showActions: "",
      onEditNode: _cache[0] || (_cache[0] = ($event: any) => (showForm.value = true)),
      onRemoveNode: removeNode,
      class: _normalizeClass(isFormCorrect.value ? '' : 'border-red-300 shadow-red-300')
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("span", null, _toDisplayString(nodeText.value), 1)
        ])
      ]),
      _: 1
    }, 8, ["nodeId", "title", "icon", "class"]),
    _createVNode(_component_UiRightPanel, {
      modelValue: showForm.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showForm).value = $event)),
      title: _ctx.$t('connectFlow.node.transferToQueue.title'),
      icon: _unref(ReplyIcon)
    }, {
      default: _withCtx(() => [
        _createElementVNode("form", {
          autocomplete: "off",
          class: "flex flex-col gap-3",
          onSubmit: _withModifiers(onSubmit, ["prevent"])
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("label", _hoisted_3, _toDisplayString(_ctx.$t('connectFlow.node.transferToQueue.select')), 1),
            _createVNode(_component_UiMultiselect, {
              name: "queue",
              label: "name",
              "value-prop": "id",
              modelValue: _unref(v$).queue.$model,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).queue.$model) = $event)),
              options: queueOptions.value,
              errors: _unref(v$).queue.$silentErrors,
              "can-clear": ""
            }, null, 8, ["modelValue", "options", "errors"])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_UiTextArea, {
              label: _ctx.$t('connectFlow.node.transferToQueue.whisper'),
              name: "whisper",
              modelValue: _unref(v$).whisper.$model,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(v$).whisper.$model) = $event))
            }, null, 8, ["label", "modelValue"])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_UiTextInput, {
              type: "number",
              name: "queueTimeoutSeconds",
              label: _ctx.$t('connectFlow.node.transferToQueue.queueTimeout.label'),
              modelValue: _unref(v$).queueTimeoutSeconds.$model as unknown as string | number,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(v$).queueTimeoutSeconds.$model as unknown as string | number) = $event)),
              errors: _unref(v$).queueTimeoutSeconds.$errors,
              "input-class": "px-4 py-3"
            }, null, 8, ["label", "modelValue", "errors"]),
            _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t('connectFlow.node.transferToQueue.queueTimeout.help')), 1)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_UiButton, {
              variant: "white",
              "text-variant": "gray-700",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (showForm.value = false))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Cancel')), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_UiButton, {
              type: "submit",
              variant: "primary",
              "text-variant": "white"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Apply')), 1)
              ]),
              _: 1
            })
          ])
        ], 32)
      ]),
      _: 1
    }, 8, ["modelValue", "title", "icon"])
  ], 64))
}
}

})